import Alpine from 'alpinejs'

type TLeaseDefaults = {
    term?: string
    yearlyKm?: string
    tire?: string
    reliefVehicle?: string
    province?: string
    ownRisk?: string
}

type TLeaseItem = {
    value: string | number
    label: string
}

type TLeaseItems = Partial<Record<keyof TLeaseDefaults, TLeaseItem[]>>

export const initPrivateLeaseCalculator = (): void => {
    Alpine.data('privateLeaseCalculator', (defaults: TLeaseDefaults = {}, slug: string, items: TLeaseItems = {}) => {
        let pending: AbortController | null = null

        return {
            isOpen: false,
            overviewOpen: false,
            activeStep: 0,
            slug,
            isCalculating: false,
            isFormSubmitted: false,
            priceData: null as Record<string, unknown> | null,
            term: defaults.term ?? '',
            yearlyKm: defaults.yearlyKm ?? '',
            tire: defaults.tire ?? '',
            reliefVehicle: defaults.reliefVehicle ?? '',
            province: defaults.province ?? 'LI',
            ownRisk: defaults.ownRisk ?? '',
            init() {
                this.calculatePrice(slug)
            },
            open() {
                this.isOpen = true
            },
            close(reset = false) {
                this.isOpen = false

                if (reset || this.activeStep === 2) {
                    this.reset()
                }
            },
            async setActiveStep(step: number) {
                const overlay = this.$root
                this.activeStep = step
                await this.$nextTick()
                overlay?.scrollTo({ top: 0, behavior: 'smooth' })
            },
            getActiveStep() {
                return this.activeStep
            },
            // The nested datamotive form swaps itself for its success page; the overview controls must step aside.
            onFormSuccess() {
                this.isFormSubmitted = true
            },
            labelFor(param: keyof TLeaseDefaults, value: unknown): string {
                return items[param]?.find((item) => String(item.value) === String(value))?.label ?? ''
            },
            leaseSummary(): Record<string, string> {
                return {
                    Looptijd: this.labelFor('term', this.term),
                    'Kilometers per jaar': this.labelFor('yearlyKm', this.yearlyKm),
                    Banden: this.labelFor('tire', this.tire),
                    'Vervangend vervoer': this.labelFor('reliefVehicle', this.reliefVehicle),
                    Provincie: this.labelFor('province', this.province),
                    'Eigen risico': this.labelFor('ownRisk', this.ownRisk),
                    Maandbedrag: `€ ${this.formatPrice(this.priceData?.value)}`,
                }
            },
            formatPrice(value: unknown): string {
                const amount = value === null || value === '' ? NaN : Number(value)

                if (!Number.isFinite(amount)) {
                    return '-'
                }

                return new Intl.NumberFormat('nl-NL', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                }).format(amount)
            },
            formatCentsAsEuro(value: unknown): string {
                const cents = Number(value)

                if (!Number.isFinite(cents)) {
                    return ''
                }

                return new Intl.NumberFormat('nl-NL', {
                    style: 'currency',
                    currency: 'EUR',
                }).format(cents / 100)
            },
            reset() {
                this.activeStep = 0
                this.term = defaults.term ?? ''
                this.yearlyKm = defaults.yearlyKm ?? ''
                this.tire = defaults.tire ?? ''
                this.reliefVehicle = defaults.reliefVehicle ?? ''
                this.province = defaults.province ?? 'LI'
                this.ownRisk = defaults.ownRisk ?? ''
            },
            async calculatePrice(vehicleSlug: string) {
                if (!vehicleSlug) {
                    return
                }

                pending?.abort()
                const controller = new AbortController()
                pending = controller
                this.isCalculating = true

                const params = new URLSearchParams({
                    Duration: this.term,
                    YearlyKm: this.yearlyKm,
                    TireProduct: this.tire,
                    OwnRiskLevel: this.ownRisk,
                    ReliefVehicleProduct: this.reliefVehicle,
                    Province: this.province,
                })

                try {
                    const response = await fetch(
                        `https://api.datamotive.nl/api/v1/vehicle/${vehicleSlug}/lease/running-calculator/calculate-price?${params}`,
                        {
                            signal: controller.signal,
                        }
                    )

                    this.priceData = await response.json()
                } catch (error) {
                    if ((error as Error).name !== 'AbortError') {
                        throw error
                    }
                } finally {
                    if (pending === controller) {
                        pending = null
                        this.isCalculating = false
                    }
                }
            },
            toggleOverview() {
                this.overviewOpen = !this.overviewOpen
            },
        }
    })
}
